import { useCallback, useEffect, useRef } from 'react';

import { kycVerifiedSelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useSubscribeAuth } from '@/utils/websocket/hooks';
import { TrustlyStatusUpdateMessage } from '@/utils/websocket/types';

import { TrustlyStatus, fetchTrustlyStatus } from '../utils/trustly-status';

type UseTrustlyStatusOptions = {
    onSuccess: (status: TrustlyStatus) => void;
    onError: (error: unknown) => void;
    onAvailableFundsGuidance?: (suggestedAmount: string) => void;
    timeoutMs?: number;
    isWebsocketActive?: boolean;
};

type UseTrustlyStatusReturn = {
    startTimerForCheckStatusApiAsFallback: (userWalletId: string) => void;
    cleanup: () => void;
};

/**
 * Hook for handling Trustly status updates via websocket with HTTP API fallback
 * @param options Configuration options for the hook
 * @returns An object with utility functions and state for handling Trustly status
 */
export const useTrustlyStatus = ({
    onSuccess,
    onError,
    onAvailableFundsGuidance,
    timeoutMs = 30000,
    isWebsocketActive = true,
}: UseTrustlyStatusOptions): UseTrustlyStatusReturn => {
    const websocketStatusReceivedRef = useRef(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
    const jurisdictionSettings = useJurisdictionStore.getState().jurisdictionSettings;
    const isTrustlyAvailableFundsGuidanceEnabled =
        jurisdictionSettings?.productConfig?.settings?.enable_trustly_available_funds_guidance;
    const { data: isKycVerified } = useAuthUserConfig({ select: kycVerifiedSelector });
    const hasSocialWallet = useHasSocialWallet();

    // Handle status updates from any source (websocket or HTTP)
    const handleStatusUpdate = useCallback(
        (status: TrustlyStatus, suggestedRetryAmount?: string) => {
            // Funds guidance requirements
            const shouldProceedWithFundsGuidance =
                isTrustlyAvailableFundsGuidanceEnabled && // Enabled in prismic
                suggestedRetryAmount && // Suggested amount provided via webhook
                isKycVerified && // Is not on Trustly KYC flow
                !hasSocialWallet && // Is not a coin package purchase
                onAvailableFundsGuidance;

            // Clear any pending timeout when we get a response
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            if (shouldProceedWithFundsGuidance) {
                onAvailableFundsGuidance(suggestedRetryAmount);
                return;
            }

            onSuccess(status);
        },
        [onSuccess, onAvailableFundsGuidance, isTrustlyAvailableFundsGuidanceEnabled, isKycVerified, hasSocialWallet]
    );

    // WebSocket message handler
    const handleWebSocketMessage = useCallback(
        (message: TrustlyStatusUpdateMessage) => {
            websocketStatusReceivedRef.current = true;
            handleStatusUpdate(message.status, message.suggested_retry_amount);
        },
        [handleStatusUpdate]
    );

    // Subscribe to Trustly status updates via websocket
    useSubscribeAuth<'trustlyStatusUpdate'>('trustly_status_update', handleWebSocketMessage, isWebsocketActive);

    // Setup the HTTP API fallback mechanism with a timer
    const startTimerForCheckStatusApiAsFallback = useCallback(
        (userWalletId: string) => {
            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Set up HTTP API fallback if websocket doesn't receive response within timeout period
            timeoutRef.current = setTimeout(() => {
                if (!websocketStatusReceivedRef.current) {
                    fetchTrustlyStatus({ userWalletId })
                        .then(response => {
                            handleStatusUpdate(response.data.status);
                        })
                        .catch(error => {
                            onError(error);
                        });
                }
            }, timeoutMs);
        },
        [handleStatusUpdate, timeoutMs, onError]
    );

    // Cleanup function to clear the timeout and reset flags
    const cleanup = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        // Reset the websocket status flag but keep the processed flag
        websocketStatusReceivedRef.current = false;
    }, []);

    // Ensure cleanup on unmount
    useEffect(() => {
        return cleanup;
    }, [cleanup]);

    return {
        startTimerForCheckStatusApiAsFallback,
        cleanup,
    };
};
